import { Tags } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface PartnerTaxonomyCardProps {
  /** Delimiter-preserving decoded category breadcrumb (e.g. "Locations>City>Churches"). Split on `>` only. */
  categoriesRaw?: string;
  /** Delimiter-preserving decoded, comma-separated tag list. Split on `,`. */
  tagsRaw?: string;
  /**
   * The structured terms the import stored (2026-09-25), which the list
   * filters run on. When present they are shown instead of the raw strings:
   * one chip per category crumb (parents are folded into their crumbs), per
   * tag and per amenity.
   */
  categories?: string[];
  tags?: string[];
  amenities?: string[];
}

/**
 * Splits a single `>`-delimited breadcrumb into its segments.
 * Never splits on `,` — a leaf segment may legitimately contain commas
 * (e.g. "Locations>City>Public Buildings>Lakes, Dams & Reservoirs").
 */
function splitCategories(categoriesRaw?: string): string[] {
  return (categoriesRaw ?? '')
    .split('>')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

function splitTags(tagsRaw?: string): string[] {
  return (tagsRaw ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

/** A parent that also appears as the head of a crumb says nothing the crumb does not. */
function foldParents(terms: string[]): string[] {
  const heads = new Set(terms.filter((t) => t.includes('>')).map((t) => t.slice(0, t.indexOf('>')).trim().toLowerCase()));
  return terms.filter((t) => t.includes('>') || !heads.has(t.trim().toLowerCase()));
}

export function PartnerTaxonomyCard({
  categoriesRaw, tagsRaw, categories: structuredCategories, tags: structuredTags, amenities,
}: PartnerTaxonomyCardProps): JSX.Element | null {
  const categories = structuredCategories && structuredCategories.length > 0
    ? foldParents(structuredCategories)
    : splitCategories(categoriesRaw);
  const tags = structuredTags && structuredTags.length > 0 ? structuredTags : splitTags(tagsRaw);
  const amenityList = amenities ?? [];

  if (categories.length === 0 && tags.length === 0 && amenityList.length === 0) {
    return null;
  }

  return (
    <Card data-testid="partner-taxonomy-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Tags className="h-5 w-5" />
          Partner Taxonomy
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {categories.length > 0 && (
          <div>
            <span className="text-sm font-medium text-muted-foreground">Partner Categories</span>
            <div className="mt-2 flex flex-wrap gap-1">
              {categories.map((category, index) => (
                <Badge key={`${String(index)}-${category}`} variant="secondary" className="text-xs">
                  {category}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {amenityList.length > 0 && (
          <div>
            <span className="text-sm font-medium text-muted-foreground">Partner Amenities</span>
            <div className="mt-2 flex flex-wrap gap-1" data-testid="partner-amenities">
              {amenityList.map((amenity, index) => (
                <Badge key={`${String(index)}-${amenity}`} variant="outline" className="text-xs">
                  {amenity}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {tags.length > 0 && (
          <div>
            <span className="text-sm font-medium text-muted-foreground">Partner Tags</span>
            <div className="mt-2 flex flex-wrap gap-1">
              {tags.map((tag, index) => (
                <Badge key={`${String(index)}-${tag}`} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
